import { useCallback } from 'react';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { connectionPagePath, servicePluginPagePath } from '@/lib/plugins/plugin-sections';
import { navigateApp } from '@/vite/navigation-store';

/** What a view opens that the core shows itself: a conversation, where an account connects. */
export type CoreOpenTarget =
  | { conversation: string }
  | { connect: string; connection?: string | undefined };

export function isCoreOpenTarget(target: object): target is CoreOpenTarget {
  return 'conversation' in target || 'connect' in target;
}

/**
 * Opens a view's `view.open` target the core shows: a conversation (an automation run's), shown
 * with its own panel; or the page where the user connects a service's account again.
 */
export function useOpenCoreTarget(): (target: CoreOpenTarget) => void {
  const opener = useConversationOpener();
  return useCallback(
    (target) => {
      if ('connect' in target) {
        // A known account: Connections, on it, its sign-in started; else the service's page.
        navigateApp(
          target.connection === undefined
            ? servicePluginPagePath(target.connect)
            : connectionPagePath(target.connection),
        );
        return;
      }
      if (opener === null) return;
      void opener.openConversation(target.conversation);
    },
    [opener],
  );
}
